import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { ROUTES } from '@/lib/constants';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Eye, EyeOff, AlertTriangle, Lock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLoginRateLimit } from '@/hooks/useLoginRateLimit';

const loginSchema = z.object({
  email: z.string().trim().email('Please enter a valid email address').max(255),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const { signIn, session, user, isLoading, isAuthReady } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('rememberMe') === 'true';
  });

  const {
    isLocked,
    remainingAttempts,
    recordFailedAttempt,
    recordSuccessfulLogin,
    canAttemptLogin,
    formatRemainingTime,
  } = useLoginRateLimit();

  // Load saved email if "Remember me" was checked
  const savedEmail = rememberMe ? localStorage.getItem('savedEmail') || '' : '';

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: savedEmail,
      password: '',
    },
  });

  useEffect(() => {
    // Wait for auth to be fully ready with both session AND user data before navigating
    // isAuthReady ensures we don't navigate until user data is fetched
    if (isAuthReady && session && user) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isAuthReady, session, user, navigate]);

  const onSubmit = async (data: LoginFormData) => {
    // Check rate limit before attempting login
    if (!canAttemptLogin()) {
      toast.error('Too many failed attempts. Please wait before trying again.');
      return;
    }

    setIsSubmitting(true);
    
    const { error } = await signIn(data.email, data.password);
    
    if (error) {
      // Record failed attempt
      await recordFailedAttempt(data.email);

      if (error.message.includes('Invalid login credentials')) {
        toast.error('Invalid email or password');
      } else if (error.message.includes('Email not confirmed')) {
        toast.error('Please check your email to confirm your account');
      } else {
        toast.error(error.message);
      }
      setIsSubmitting(false);
      return;
    }

    // Record successful login (resets rate limit)
    await recordSuccessfulLogin(data.email);

    // Store remember me preference and email
    localStorage.setItem('rememberMe', rememberMe.toString());
    if (rememberMe) {
      localStorage.setItem('savedEmail', data.email);
    } else {
      localStorage.removeItem('savedEmail');
    }

    toast.success('Welcome back!');
    // Set transitioning to show full-page spinner while auth state settles
    // Navigation is handled by the useEffect that watches isAuthReady
    setIsTransitioning(true);
  };

  // Show full-page spinner during initial auth load or after successful login
  if (isLoading || isTransitioning) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const showWarning = !isLocked && remainingAttempts <= 3 && remainingAttempts > 0;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 safe-y">
      {/* Company Logos */}
      <div className="mb-8 flex flex-col items-center gap-6">
        <img
          src="/images/DitanLogo.png"
          alt="Ditan Attorneys"
          className="h-14 w-auto object-contain"
        />
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-xs">in partnership with</span>
        </div>
        <img
          src="/images/Konica-Minolta-Logo.png"
          alt="Konica Minolta"
          className="h-10 w-auto object-contain"
        />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-heading">Sign in</CardTitle>
          <CardDescription>
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Lockout Alert */}
          {isLocked && (
            <Alert variant="destructive" className="mb-4">
              <Lock className="h-4 w-4" />
              <AlertDescription className="ml-2">
                Too many failed login attempts. Please try again in{' '}
                <span className="font-semibold">{formatRemainingTime()}</span>
              </AlertDescription>
            </Alert>
          )}

          {/* Warning Alert - approaching limit */}
          {showWarning && (
            <Alert className="mb-4 border-warning bg-warning/10">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="ml-2 text-warning">
                {remainingAttempts} login {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining before temporary lockout
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        disabled={isLocked}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          disabled={isLocked}
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLocked}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                    disabled={isLocked}
                  />
                  <Label
                    htmlFor="remember-me"
                    className="text-sm font-normal text-muted-foreground cursor-pointer"
                  >
                    Remember me
                  </Label>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full touch-target" 
                disabled={isSubmitting || isLocked}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Sign in'}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Link
            to={ROUTES.FORGOT_PASSWORD}
            className="text-sm text-muted-foreground hover:text-primary"
          >
            Forgot your password?
          </Link>
          <div className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to={ROUTES.REGISTER} className="text-primary hover:underline">
              Sign up
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
